<template>
  <PageContainer
    :class="$style.container"
  >
    <template v-slot:body>
      <Container>
        <Card
          v-if="!loading"
          :class="$style.report"
        >
          <NotificationInPage
            data-test-id="ValueReportMainPanel__errorNotification"
            :notification="$options.errorNotification"
            :showNotification="showErrorNotification"
            @close="handleClose()"
          />
          <template v-slot:header>
            <ParagraphBody
              :class="$style.header"
            >
              Please help us make this a better experience for you and admins like you
              by filling out
              <a
                href="https://www.surveymonkey.com/r/DY9XF6M"
                target="_blank"
              >this survey</a>, thank you.
            </ParagraphBody>
            <ParagraphBody
              :class="$style.header"
            >
              Here’s how you’ve been using JumpCloud. This summary helps you better understand how
              JumpCloud is working for you and your organization. <strong>This data is based on the
                status of your JumpCloud account (org) at the end of the previous calendar month.
              </strong>
            </ParagraphBody>
          </template>
          <template v-slot:body>
            <div
              :class="$style.body"
            >
              <OverviewCard
                data-test-id="ValueReportMainPanel__overviewCard"
                :metrics="metrics.overviewMetrics"
              />
              <SecurityCard
                data-test-id="ValueReportMainPanel__securityCard"
                :metrics="metrics.securityCompliance"
              />
              <EfficiencyCard
                data-test-id="ValueReportMainPanel__efficiencyCard"
                :metrics="metrics.efficiency"
              />
              <VendorConsolidationCard
                v-if="showVendorConsolidation"
                data-test-id="ValueReportMainPanel__vendorConsolidationCard"
                :metrics="metrics.vendorConsolidation"
                :usersCount="totalUsers"
              />
            </div>
          </template>
        </Card>
        <LoadingOverlay
          :isCloud="true"
          :isLight="true"
          :visible="loading"
        />
      </Container>
    </template>
  </PageContainer>
</template>

<script>
import {
  Card,
  Container,
  LoadingOverlay,
  NotificationInPage,
  PageContainer,
  ParagraphBody,
  Types,
} from '@jumpcloud/ui-components';
import { UserRoleConsts } from 'client/util/Constants';
import { mapGetters } from 'vuex';
import rbacUtil from 'client/util/rbacUtils';

import EfficiencyCard from './EfficiencyCard.vue';
import OverviewCard from './OverviewCard.vue';
import SecurityCard from './SecurityCard.vue';
import ValueReportApiWrapper from './ValueReportApiWrapper';
import VendorConsolidationCard from './VendorConsolidationCard.vue';

const { NotificationConfig } = Types;
const { administrator } = UserRoleConsts;

const errorNotification = NotificationConfig({
  icon: 'error',
  message: 'Unable to load Value Report.',
  showCloseButton: true,
  title: 'Error',
  type: 'error',
});

export default {
  name: 'ValueReportMainPanel',

  components: {
    Card,
    Container,
    EfficiencyCard,
    LoadingOverlay,
    NotificationInPage,
    OverviewCard,
    PageContainer,
    ParagraphBody,
    SecurityCard,
    VendorConsolidationCard,
  },

  errorNotification,

  data() {
    return {
      loading: false,
      metrics: {},
      showErrorNotification: false,
    };
  },

  computed: {
    ...mapGetters('SettingsModule', {
      user: 'getUser',
    }),

    showVendorConsolidation() {
      return this.user.roleName !== administrator;
    },

    totalUsers() {
      let userCount = 0;
      if (this.metrics
        && this.metrics.overviewMetrics
        && this.metrics.overviewMetrics.length > 0) {
        const users = this.metrics.overviewMetrics.find(metric => metric.label === 'Users');
        userCount = users && users.value;
      }
      return userCount;
    },

    usersCount() {
      return this.metrics.overviewMetrics && this.metrics.overviewMetrics.length > 0
        ? this.metrics.overviewMetrics[0].value
        : 0;
    },
  },

  created() {
    this.loadData();
  },

  methods: {
    handleClose() {
      this.showErrorNotification = false;
    },

    async loadData() {
      this.setLoading(true);

      rbacUtil.dontUseNotifyUtil();
      try {
        this.metrics = await ValueReportApiWrapper.fetchAll(this.user.roleName);
      } catch (err) {
        this.showErrorNotification = true;
      } finally {
        this.setLoading(false);
      }
    },

    setLoading(isLoading) {
      this.loading = isLoading;
    },
  },
};
</script>

<style module scoped>
  .container {
    background-color: var(--jcWhite);
  }

  .report {
    display: flex;
    flex-direction: column;
  }

  .header.header,
  .body {
    margin: 0 auto;
    max-width: var(--jc-size-45);
  }
</style>
